import { __decorate } from "tslib";
import { Prop, Mixins, Component, Watch } from 'vue-property-decorator';
import moment from 'moment';
import BaseCloseBtn from '@/components/v2/BaseCloseBtn';
import AlertMixin from '@/helpers/vue_mixins/AlertMixin';
import FetchShowcaseProducts from '@/plugins/graphql/queries/v2/affiliate/automation-fetch-products.gql';
import FetchAutomationAvailableProducts from '@/plugins/graphql/queries/v2/non-affiliate-automation-products.gql';
import TheAlert from '@/views/shared/TheAlert.vue';
import ActionButton from '@/components/action_button/Index.vue';
import BaseSearchInput from '@/components/v2/BaseSearchInput';
import PaginatorMixin from '@/helpers/mixins/PaginatorMixin';
import ViewportSizeMixin from '@/helpers/mixins/ViewportSizeMixin';
import Paginator from '@/components/v2/Paginator.vue';
import { EcommerceProductModel } from '@/helpers/entities/models/EcommerceProductModel';
import { AFFILIATE } from '@/helpers/constants/CustomBundleKeys';
import { debounce } from 'lodash';
let AffiliateProductDialog = class AffiliateProductDialog extends Mixins(AlertMixin, PaginatorMixin, ViewportSizeMixin) {
    constructor() {
        super(...arguments);
        this.products = null;
        this.search = null;
        this.testRadioSelection = null;
        this.message = null;
        this.selectedProducts = this.previouslySelectedProducts;
        this.selections = {};
    }
    truncateTitle(name) {
        if (name.length > 40) {
            // Truncate the name to 40 characters and add ellipsis
            return `${name.substring(0, 40)}...`;
        }
        // Return the original name if it's already shorter than or equal to 40 characters
        return name;
    }
    nextPage(event) {
        this.fetchRecordsFrom(event);
        this.updateSelections();
    }
    previousPage(event) {
        this.fetchRecordsFrom(event);
        this.updateSelections();
    }
    onToggleChanged(newValue) {
        if (newValue && this.products === null) {
            this.message = 'Fetching products...';
            this.$apollo
                .query({
                query: this.productQuery(),
                variables: {
                    appUUID: this.appUuid,
                    after: null,
                    first: 10,
                    ...this.buildSearchKeywordParams(),
                },
                fetchPolicy: 'network-only',
            })
                .then(({ data }) => {
                const { edges, pageInfo, totalCount } = data.fetchShowcaseProducts || data.fetchAutomationAvailableProducts;
                this.pageInfo = pageInfo;
                this.totalCount = totalCount;
                this.products = edges.map((product) => new EcommerceProductModel(product.node));
                this.updateSelections();
                if (this.products?.length === 0) {
                    this.message = 'No available products';
                }
                else {
                    this.message = null;
                }
            })
                .catch((error) => {
                this.errorNotification(error.message);
                this.message = `Error in fetching products: ${error.message}`;
            });
        }
    }
    toggleSelection(checked, item) {
        this.$set(this.selections, item.name, checked);
        if (checked) {
            // If the checkbox is checked, add the item to the selectedProducts array
            this.selectedProducts.push(item);
        }
        else {
            // If the checkbox is unchecked, remove the item from the selectedProducts array
            const index = this.selectedProducts.findIndex((product) => product.name === item.name);
            if (index !== -1) {
                this.selectedProducts.splice(index, 1);
            }
        }
    }
    onSearch() {
        this.message = 'Searching products...';
        this.$apollo
            .query({
            query: this.productQuery(),
            variables: {
                appUUID: this.appUuid,
                after: null,
                first: 10,
                ...this.buildSearchKeywordParams(),
            },
            fetchPolicy: 'network-only',
        })
            .then(({ data }) => {
            const { edges, pageInfo, totalCount } = data.fetchShowcaseProducts || data.fetchAutomationAvailableProducts;
            this.pageInfo = pageInfo;
            this.totalCount = totalCount;
            this.products = edges.map((product) => new EcommerceProductModel(product.node));
            this.updateSelections();
            this.$refs.paginator.start = 1;
            this.$refs.paginator.cursorStack = [];
            if (this.products?.length === 0) {
                this.message = 'No result/s found';
            }
            else {
                this.message = null;
            }
        })
            .catch((error) => {
            this.errorNotification(error.message);
            this.message = `Error in fetching products: ${error.message}`;
        });
    }
    onPaginationChanged(newValue) {
        this.message = 'Fetching products...';
        this.$apollo
            .query({
            query: this.productQuery(),
            variables: {
                appUUID: this.appUuid,
                after: newValue,
                first: 10,
                ...this.buildSearchKeywordParams(),
            },
            fetchPolicy: 'network-only',
        })
            .then(({ data }) => {
            const { edges, pageInfo, totalCount } = data.fetchShowcaseProducts || data.fetchAutomationAvailableProducts;
            this.pageInfo = pageInfo;
            this.totalCount = totalCount;
            this.products = edges.map((product) => new EcommerceProductModel(product.node));
            this.updateSelections();
            if (this.products?.length === 0) {
                this.message = 'No available products';
            }
            else {
                this.message = null;
            }
        })
            .catch((error) => {
            this.errorNotification(error.message);
            this.message = `Error in fetching products: ${error.message}`;
        });
    }
    handlePreviouslySelectedProductsChange(newValue) {
        this.selectedProducts = [...newValue];
        // Reset selections object
        this.selections = {};
        // Check if newValue is not null or undefined
        if (newValue && this.products) {
            // Iterate over products and update selections based on previouslySelectedProducts
            this.products.forEach((product) => {
                // Initialize selection state based on whether the product is in previouslySelectedProducts
                this.$set(this.selections, product.name, newValue.some((p) => p.name === product.name));
            });
        }
    }
    updateSelections() {
        if (this.products) {
            this.products.forEach((product) => {
                // Initialize selection state based on whether the product is in selectedProducts
                this.selections[product.name] = this.selectedProducts.some((p) => p.name === product.name);
            });
        }
    }
    productQuery() {
        if (this.bundleKey === AFFILIATE) {
            return FetchShowcaseProducts;
        }
        return FetchAutomationAvailableProducts;
    }
    buildSearchKeywordParams() {
        if (this.bundleKey === AFFILIATE) {
            return { productName: this.search };
        }
        return { keyword: this.search };
    }
    humanReadableDate(date) {
        return moment(date).fromNow();
    }
    hideProducts() {
        this.$refs.paginator.start = 1;
        this.$refs.paginator.cursorStack = [];
        this.message = null;
        this.products = null;
        this.$emit('cancel');
    }
    confirm() {
        if (this.selectedProducts.length > 0) {
            this.$emit('selectedProducts', this.selectedProducts);
            this.$emit('cancel');
        }
        else {
            this.errorNotification('Please select a Product.');
        }
    }
    getImage(img) {
        return img?.galleryBlob.originalUrl;
    }
};
__decorate([
    Prop({ type: Boolean, default: true })
], AffiliateProductDialog.prototype, "toggle", void 0);
__decorate([
    Prop({ type: String, default: null })
], AffiliateProductDialog.prototype, "appUuid", void 0);
__decorate([
    Prop({ type: String, default: null })
], AffiliateProductDialog.prototype, "bundleKey", void 0);
__decorate([
    Prop({ type: Array, default: () => [] })
], AffiliateProductDialog.prototype, "previouslySelectedProducts", void 0);
__decorate([
    Watch('toggle')
], AffiliateProductDialog.prototype, "onToggleChanged", null);
__decorate([
    Watch('startCursor')
], AffiliateProductDialog.prototype, "onPaginationChanged", null);
__decorate([
    Watch('previouslySelectedProducts', { immediate: true, deep: true })
], AffiliateProductDialog.prototype, "handlePreviouslySelectedProductsChange", null);
AffiliateProductDialog = __decorate([
    Component({
        components: {
            BaseCloseBtn,
            TheAlert,
            Paginator,
            ActionButton,
            BaseSearchInput,
        },
        methods: {
            // eslint-disable-next-line
            debounceSearch: debounce(function () {
                this.onSearch();
            }, 500),
        },
    })
], AffiliateProductDialog);
export default AffiliateProductDialog;
